import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {LAST_UPDATED_VALUE_OPTIONS} from 'shells/control-bar/constants';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {
    getArchivedValue,
    getCampaignStatusesForQuery,
    getLastUpdatedValue,
} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {NewsletterStubFragment as Newsletter} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import {writeLocalStorage} from '../../../util/localStorage';

import {GetSessionUser} from '../../core/get-session-user';
import {PageContainer, PageTitleBar} from '../../layout';

import {checkIsFiltering} from '../email-marketing-helpers';
import {getNewsletterListQueryVariables} from '../graphql-wrappers/helpers';
import {NewsletterListQuery} from '../graphql-wrappers/newsletter/newsletter-list-query';
import {NewsletterMutations} from '../graphql-wrappers/newsletter/newsletter-mutations';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {NewslettersBoardView} from './newsletters-board-view';
import {NewslettersListView} from './newsletters-list-view';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    newsletters: Newsletter[];
    onCreateNewsletter: (name: string) => Promise<any>;
    onDeleteNewsletter: (newsletterId: string) => Promise<any>;
    onRestoreNewsletter: (newsletterId: string) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
    searchValue: string;
    userId: string;
};

const NEWSLETTERS_VIEW_SEARCH = 'NEWSLETTERS_VIEW_SEARCH';

function NewslettersListPageComponent(props: Props) {
    const [isAddingNewNewsletter, setIsAddingNewNewsletter] = React.useState<boolean>(false);
    const [isSavingNewNewsletter, setIsSavingNewNewsletter] = React.useState<boolean>(false);

    const selectedTab = props.urlParams.view || 'board';

    React.useEffect(() => {
        writeLocalStorage(NEWSLETTERS_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const isFiltering = checkIsFiltering(props.activeFilters, props.searchValue);

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({
                view: 'board',
            }),
            component: (
                <NewslettersBoardView
                    isLoading={props.isLoading}
                    // @ts-expect-error
                    error={props.error}
                    isFiltering={isFiltering}
                    isShowingArchived={isShowingArchived}
                    newsletters={props.newsletters}
                    searchValue={props.searchValue}
                    onDeleteNewsletter={props.onDeleteNewsletter}
                    onRestoreNewsletter={props.onRestoreNewsletter}
                    onCreateNewsletter={() => {
                        setIsAddingNewNewsletter(true);
                    }}
                    setIsAddingNewNewsletter={setIsAddingNewNewsletter}
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({
                view: 'table',
            }),
            component: (
                <NewslettersListView
                    isLoading={props.isLoading}
                    error={props.error}
                    newsletters={props.newsletters}
                    sortString={props.urlParams.sort}
                    isFiltering={isFiltering}
                    isShowingArchived={isShowingArchived}
                    onChangeSort={(newSortString) => {
                        props.onParamChange({sort: newSortString}, true);
                    }}
                    setIsAddingNewNewsletter={setIsAddingNewNewsletter}
                />
            ),
        },
    ];

    return (
        <PageContainer
            title='Newsletters'
            pageTitleBar={
                <PageTitleBar
                    title='Newsletters'
                    subheaderPrimaryText='An ongoing series of messages that follow a similar theme'
                    buttons={
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                setIsAddingNewNewsletter(true);
                            }}
                        >
                            Create a newsletter
                        </Button>
                    }
                />
            }
            controlBar={
                <ListPageControlBar
                    archiveTab={{
                        isActive: isShowingArchived,
                        onChange: () => {
                            props.onUpdateFilters({
                                archived: isShowingArchived ? null : true,
                            });
                        },
                    }}
                    tabs={{
                        selectedTab: selectedTab,
                        options: tabOptions,
                    }}
                    leftSection={
                        <ExpandedFilterBar
                            lastUpdatedFilterProps={{
                                lastUpdatedFilter: safelyGetFilterByKey(
                                    props.activeFilters,
                                    'lastUpdated'
                                ),
                                // @ts-expect-error
                                lastUpdatedOptions: LAST_UPDATED_VALUE_OPTIONS,
                            }}
                            justMineFilterProps={{
                                isFilteringOnCreatorUserId: isFilteringOnCreatorUserId,
                                userId: props.userId,
                            }}
                            searchFilterProps={{
                                searchValue: props.searchValue,
                                onUpdateSearch: (newSearchValue) => {
                                    props.onParamChange({q: newSearchValue}, true);
                                },
                            }}
                            isFiltering={isFiltering}
                            noun='newsletter'
                            count={props.newsletters.length}
                            onUpdateFilters={props.onUpdateFilters}
                            onRemoveAllFilters={props.onRemoveAllFilters}
                            activeFilters={props.activeFilters}
                        />
                    }
                />
            }
        >
            <MarketingStatsBanner isImportUnsubscribesBannerNeeded={true} />
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
            {isAddingNewNewsletter ? (
                <SingleInputModal
                    requireValue={true}
                    title='Name this newsletter'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder='e.g. Product updates'
                    buttonText='Create'
                    isSaving={isSavingNewNewsletter}
                    onSave={(newName) => {
                        setIsSavingNewNewsletter(true);
                        props.onCreateNewsletter(newName).then(() => {
                            setIsAddingNewNewsletter(false);
                            setIsSavingNewNewsletter(false);
                        });
                    }}
                    onClose={() => {
                        setIsAddingNewNewsletter(false);
                    }}
                />
            ) : null}
        </PageContainer>
    );
}

export function NewslettersListPage(props: OwnProps) {
    // We've hardcoded the server's sort for now - table sorting is currently
    // handled on the client and stored in the URL
    const sortValue = 'MODIFIED_TIME_DESC';
    const searchValue = props.urlParams.q || '';
    const isArchived = getArchivedValue(props.activeFilters);

    const queryVariables = getNewsletterListQueryVariables(isArchived, sortValue, searchValue);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);
    const statuses = getCampaignStatusesForQuery(props.activeFilters);

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                return user ? (
                    <NewsletterListQuery
                        isArchived={isArchived}
                        sortOrder={sortValue}
                        searchQuery={searchValue}
                        lastUpdatedTime={lastUpdatedTime}
                        creatorUserId={isFilteringOnCreatorUserId ? user.id : undefined}
                        statuses={statuses && statuses.length ? statuses : undefined}
                    >
                        {({newsletters, isLoading, error}) => (
                            <NewsletterMutations>
                                {({onCreate, onDelete, onRestore}) => (
                                    <NewslettersListPageComponent
                                        {...props}
                                        isLoading={isLoading}
                                        onChangeTab={(newTab: string) => {
                                            if (newTab === 'archived') {
                                                appHistory.push(
                                                    '/email-marketing/newsletters/archived'
                                                );
                                            } else {
                                                appHistory.push('/email-marketing/newsletters');
                                            }
                                        }}
                                        newsletters={newsletters}
                                        error={error}
                                        // @ts-expect-error
                                        onCreateNewsletter={(name) =>
                                            onCreate(name, queryVariables)
                                        }
                                        // @ts-expect-error
                                        onDeleteNewsletter={(newsletterId) =>
                                            onDelete(newsletterId, queryVariables)
                                        }
                                        // @ts-expect-error
                                        onRestoreNewsletter={(newsletterId) =>
                                            onRestore(newsletterId, queryVariables)
                                        }
                                        searchValue={searchValue}
                                        sortValue={sortValue}
                                        userId={user.id}
                                    />
                                )}
                            </NewsletterMutations>
                        )}
                    </NewsletterListQuery>
                ) : null;
            }}
        </GetSessionUser>
    );
}
